import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Add, Delete, Security } from '@mui/icons-material';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { DataTable } from '../components/data-table';
import { LoadingState } from '../components/loading-state';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';
import {
  useGetCapabilitiesQuery,
  useCreateCapabilityMutation,
  useDeleteCapabilityMutation,
} from '../store/hooks';
import type { CapabilityResponse } from '@store/generated/admin';

export default function CapabilitiesListPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCapability, setSelectedCapability] = useState<CapabilityResponse | null>(null);
  const [newCapability, setNewCapability] = useState({ name: '', description: '' });

  const { data, isLoading, error, refetch } = useGetCapabilitiesQuery();
  const [createCapability, { isLoading: isCreating }] = useCreateCapabilityMutation();
  const [deleteCapability, { isLoading: isDeleting }] = useDeleteCapabilityMutation();

  const capabilities = data?.capabilities || [];

  const filteredCapabilities = useMemo(() => {
    if (!searchInput.trim()) return capabilities;
    const searchLower = searchInput.toLowerCase();
    return capabilities.filter(
      cap =>
        cap.name.toLowerCase().includes(searchLower) ||
        cap.description?.toLowerCase().includes(searchLower)
    );
  }, [capabilities, searchInput]);

  const handleRowClick = useCallback(
    (cap: CapabilityResponse) => {
      navigate(`/admin-v2/capability/${cap.capability_id}`);
    },
    [navigate]
  );

  const handleCreateOpen = useCallback(() => {
    setNewCapability({ name: '', description: '' });
    setCreateDialogOpen(true);
  }, []);

  const handleCreateClose = useCallback(() => {
    setCreateDialogOpen(false);
    setNewCapability({ name: '', description: '' });
  }, []);

  const handleCreateSubmit = useCallback(async () => {
    if (!newCapability.name.trim()) return;

    try {
      await createCapability({
        createCapabilityRequest: {
          name: newCapability.name.trim(),
          description: newCapability.description.trim() || null,
        },
      }).unwrap();
      handleCreateClose();
      refetch();
    } catch (err) {
      console.error('Failed to create capability:', err);
    }
  }, [newCapability, createCapability, handleCreateClose, refetch]);

  const handleDeleteClick = useCallback((cap: CapabilityResponse, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCapability(cap);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedCapability(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedCapability) return;

    try {
      await deleteCapability({
        capabilityId: selectedCapability.capability_id,
      }).unwrap();
      handleDeleteClose();
      refetch();
    } catch (err) {
      console.error('Failed to delete capability:', err);
    }
  }, [selectedCapability, deleteCapability, handleDeleteClose, refetch]);

  const columns = useMemo(
    () => [
      {
        key: 'name' as const,
        label: 'Name',
        render: (value: unknown) => (
          <div className="flex items-center">
            <Security className="h-5 w-5 text-gray-400 mr-2" />
            <span className="font-medium text-gray-900">{value as string}</span>
          </div>
        ),
      },
      {
        key: 'description' as const,
        label: 'Description',
        render: (value: unknown) => (
          <span className="text-gray-600">{(value as string) || '-'}</span>
        ),
      },
      {
        key: 'created_at' as const,
        label: 'Created',
        render: (value: unknown) => {
          if (!value) return <span className="text-gray-400">-</span>;
          const date = new Date(value as string);
          return (
            <div className="text-sm">
              <div className="text-gray-900">{formatDateTime(date)}</div>
              <div className="text-gray-500">{formatRelativeTime(date)}</div>
            </div>
          );
        },
      },
      {
        key: 'capability_id' as const,
        label: 'Actions',
        render: (_: unknown, cap: CapabilityResponse) => (
          <Tooltip title="Delete capability">
            <IconButton
              size="small"
              onClick={e => handleDeleteClick(cap, e)}
              color="error"
              aria-label="Delete capability"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [handleDeleteClick]
  );

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Error loading capabilities</h2>
            <p className="text-gray-600 mt-2">Unable to fetch capabilities list.</p>
            <Button onClick={() => navigate('/admin-v2')} className="mt-4">
              Back to Search
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>
        <div className="mb-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Capabilities</h1>
            <Button variant="contained" startIcon={<Add />} onClick={handleCreateOpen}>
              Create Capability
            </Button>
          </div>

          <Card className="mb-6">
            <CardContent>
              <TextField
                fullWidth
                placeholder="Search capabilities by name or description..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                size="small"
              />
            </CardContent>
          </Card>

          {isLoading && !data ? (
            <LoadingState message="Loading capabilities..." />
          ) : (
            <DataTable
              title="Capabilities"
              columns={columns}
              data={filteredCapabilities}
              total={filteredCapabilities.length}
              loading={isLoading}
              onRowClick={handleRowClick}
              emptyMessage="No capabilities found"
            />
          )}
        </div>
      </main>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCreateClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create Capability</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={newCapability.name}
            onChange={e => setNewCapability(prev => ({ ...prev, name: e.target.value }))}
            required
            helperText="Unique identifier for this capability (e.g., 'jotcoach', 'voice_enrollment')"
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={newCapability.description}
            onChange={e => setNewCapability(prev => ({ ...prev, description: e.target.value }))}
            helperText="Optional description of what this capability enables"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateClose}>Cancel</Button>
          <Button
            onClick={handleCreateSubmit}
            variant="contained"
            disabled={!newCapability.name.trim() || isCreating}
          >
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>Delete Capability</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the capability "{selectedCapability?.name}"? This action
            cannot be undone and may affect bundles that include this capability.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
